import React, { ComponentPropsWithoutRef, memo, ReactNode } from 'react'
import {
  Controller,
  ControllerProps,
  FieldErrors,
  FieldValues,
  get,
} from 'react-hook-form'

import { Checkbox } from '@surglogs-app/ui'

import { DefaultFormField, DefaultFormFieldProps } from './DefaultFormField'

interface CheckboxFieldProps<TValuesModel extends FieldValues>
  extends Omit<
      ComponentPropsWithoutRef<typeof Checkbox>,
      'name' | 'defaultValue'
    >,
    Pick<
      ControllerProps<TValuesModel>,
      'shouldUnregister' | 'control' | 'name' | 'defaultValue'
    > {
  errors?: FieldErrors<any>
  warnings?: Record<string, any>
  label?: ReactNode | string
  formFieldProps?: Omit<
    DefaultFormFieldProps,
    'name' | 'errors' | 'warnings' | 'children'
  >
}

const Component = <TValuesModel extends FieldValues>({
  name,
  label,
  defaultValue,
  control,
  errors,
  onChange,
  warnings,
  invalid,
  warning,
  shouldUnregister,
  ...rest
}: CheckboxFieldProps<TValuesModel>) => (
  <Controller<TValuesModel>
    {...{
      name,
      control,
      defaultValue,
      shouldUnregister,
      render: ({ field: { onBlur, onChange: _onChange, value } }) => (
        <Checkbox
          {...{
            ...rest,
            onBlur,
            onChange: (event) => {
              _onChange(!value)
              onChange?.(event)
            },
            checked: value === true,
            invalid: invalid ?? get(errors || {}, name),
            warning: warning ?? get(warnings || {}, name),
          }}>
          {label ?? ''}
        </Checkbox>
      ),
    }}
  />
)

export const CheckboxField = memo(
  <TValuesModel extends FieldValues = FieldValues>({
    formFieldProps = undefined,
    ...rest
  }: CheckboxFieldProps<TValuesModel>) => {
    if (formFieldProps) {
      return (
        <DefaultFormField
          {...{
            ...formFieldProps,
            name: rest.name,
            errors: rest.errors,
            warnings: rest.warnings,
            invalid: rest.invalid,
          }}>
          <Component {...rest} />
        </DefaultFormField>
      )
    }

    return <Component {...rest} />
  },
) as typeof Component
